/* cSpell:ignore regexify */

import React from 'react';
import { Text } from 'react-native';
import regexifyString from 'regexify-string';
import { PhoneLink } from '../../PhoneLink';

import { TextLink } from '../../TextLink';

export const TextLinkify = (props: TextLinkifyProps) => (
  <Text>{linkifyText(props.children, props.regex)}</Text>
);

export const TextLinkifyTags = ({
  children,
  links,
  ...rest
}: TextLinkifyTagsProps) => (
  <Text {...rest}>{linkifyTags(children, links, rest.size, rest.style)}</Text>
);

// ─── UTILS ──────────────────────────────────────────────────────────────────────

function linkifyText(text: string, urlRegex = /(((https?:\/\/)|(www\.))\S+)/g) {
  return regexifyString({
    pattern: urlRegex,
    decorator: (url, index) => (
      <TextLink key={index} href={withHttpsProtocol(url)}>
        {url}
      </TextLink>
    ),
    input: text,
  }).filter(withoutEmptyStrings);
}

const linkifyTags = (
  text: string,
  links: TextLinkifyTagsProps['links'],
  size: React.ComponentProps<typeof TextLink>['size'],
  style: React.ComponentProps<typeof TextLink>['style'],
) =>
  regexifyString({
    pattern: /{(.+?)}/g,
    decorator(tag, index) {
      const currentTag = links.find((link) => link.tag === tag.slice(1, -1));

      if (!currentTag) return '';

      // Phone links requires special treatment for native devices.
      if (currentTag.href?.startsWith('tel')) {
        <PhoneLink
          key={index}
          protocol="tel"
          phoneNumber={currentTag.href.split('tel:')[1]}
        >
          {currentTag.description}
        </PhoneLink>;
      }

      return (
        <TextLink
          key={index}
          size={size}
          style={style}
          href={currentTag.href}
          newTab={currentTag.newTab}
          onPress={currentTag.onPress}
        >
          {currentTag.description}
        </TextLink>
      );
    },
    input: text,
  }).filter(withoutEmptyStrings);

function withoutEmptyStrings(chunk: string | React.ReactElement) {
  return chunk !== '';
}

export function withHttpsProtocol(url: string) {
  if (url.startsWith('http')) return url;

  return `https://${url}`;
}

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type TextLinkifyProps = React.ComponentProps<typeof TextLink> & {
  readonly children: string;
  readonly regex?: RegExp;
};

type TextLinkifyTagsProps = React.ComponentProps<typeof TextLink> & {
  readonly children: string;
  readonly links: ReadonlyArray<{
    readonly tag: string;
    readonly description: string;
    readonly href?: string;
    readonly newTab?: boolean;
    readonly onPress?: () => void;
  }>;
};
